/**
 * Persona-adaptive dashboard layout: how the tiles the API returned are grouped and ordered for the
 * caller's role. Layout only - this never hides a tile (the server decides who sees what; CLAUDE.md
 * section 6 "do not filter in the client"). A tile the layout does not know about still renders, under
 * its category heading, so a tile added by the backend needs no change here.
 *
 * Cites .claude/design/dashboard-redesign.md "SPOC dashboard shape" (the SPOC headline tiles) and the
 * tile catalog. The spec also lists tiles that are not built (non-conformities open, documents overdue,
 * days to certification) - see "Spec amendment (4 Oct 2026)" in that doc; none are invented here.
 */
import { Role } from '../constants/roles';
import { DASHBOARD_CATEGORY_LABELS } from '../constants/dashboardTiles';

const SPOC_SECTIONS = [
  {
    key: 'headline',
    title: 'Engagement at a glance',
    tileIds: ['gap.readiness_percent', 'governance.maturity_score', 'risk.high_severity_count', 'audit.findings_open'],
  },
  { key: 'progress', title: 'Delivery progress', tileIds: ['control.implemented_count', 'gap.controls_in_scope'] },
];

const evidenceSection = { key: 'evidence', title: 'Evidence and monitoring', tileIds: ['evidence.documents_count', 'evidence.documents_scored', 'evidence.incident_count'] };
const riskAuditSection = {
  key: 'risk_audit',
  title: 'Risk and audit follow-up',
  tileIds: ['risk.high_severity_count', 'risk.treatment_plans_overdue', 'risk.coverage_vs_applicable', 'audit.findings_open', 'audit.cap_actions_overdue'],
};
const deliverySection = {
  key: 'delivery',
  title: 'Control delivery',
  tileIds: ['control.in_review_count', 'control.approved_this_month', 'control.implemented_count', 'gap.controls_in_scope', 'gap.readiness_percent'],
};
const teamSection = { key: 'team', title: 'Team responsiveness', tileIds: ['gap.advisors_engaged', 'gap.delegates_responsive'] };

// The Lead Advisor approves AI tiles, so those lead; everyone else only reads them.
const LEAD_ADVISOR_SECTIONS = [
  // Only AI tiles the server says are waiting for approval; the rest fall to 'AI summaries' below.
  { key: 'review', title: 'Needs your review', tileIds: ['governance.maturity_score', 'risk.trend_narrative'], when: (tile) => tile.availability === 'awaiting_approval' },
  riskAuditSection,
  deliverySection,
  evidenceSection,
  teamSection,
  { key: 'ai_summaries', title: 'AI summaries', tileIds: ['governance.maturity_score', 'risk.trend_narrative'] },
];

const READ_ONLY_STAFF_SECTIONS = [
  deliverySection,
  teamSection,
  evidenceSection,
  riskAuditSection,
  { key: 'ai_summaries', title: 'AI summaries', tileIds: ['governance.maturity_score', 'risk.trend_narrative'] },
];

export function sectionDefinitionsForRole(role) {
  if (role === Role.COMPANY_SPOC) return SPOC_SECTIONS;
  if (role === Role.LEAD_ADVISOR) return LEAD_ADVISOR_SECTIONS;
  if (role === Role.ADVISOR || role === Role.ADMIN) return READ_ONLY_STAFF_SECTIONS;
  return [];
}

/** @returns {{ key: string, title: string, tiles: object[] }[]} non-empty sections, in display order. */
export function buildDashboardSections(tiles, role) {
  const list = Array.isArray(tiles) ? tiles : [];
  const byId = new Map(list.map((t) => [t.tileId, t]));
  const placed = new Set();
  const sections = [];

  sectionDefinitionsForRole(role).forEach((def) => {
    const sectionTiles = def.tileIds
      .filter((id) => byId.has(id) && !placed.has(id) && (!def.when || def.when(byId.get(id))))
      .map((id) => {
        placed.add(id);
        return byId.get(id);
      });
    if (sectionTiles.length) sections.push({ key: def.key, title: def.title, tiles: sectionTiles });
  });

  const rest = list.filter((t) => !placed.has(t.tileId));
  const byCategory = rest.reduce((acc, t) => {
    const cat = t.category || 'other';
    (acc[cat] = acc[cat] || []).push(t);
    return acc;
  }, {});
  Object.entries(byCategory).forEach(([cat, catTiles]) => {
    sections.push({ key: `category-${cat}`, title: DASHBOARD_CATEGORY_LABELS[cat] || cat, tiles: catTiles });
  });
  return sections;
}
